'use client'

import { ReactNode, useEffect, useState } from 'react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { publishPortalSessionDeadOnUnauthorized } from '@/domains/requirementsPortal/portalSessionDeadEvent'
import { HttpError } from '@hello-aria/api-client'
import { ObservabilityProvider } from '@hello-aria/observability/client'
import Spinner from '@/shared/components/Spinner/Spinner'

const shouldEnableMocks =
    process.env.NEXT_PUBLIC_MOCK === 'true' && process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production'

export const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: publishPortalSessionDeadOnUnauthorized }),
    mutationCache: new MutationCache({ onError: publishPortalSessionDeadOnUnauthorized }),
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                if (error instanceof HttpError && error.status >= 400 && error.status < 500) {
                    return false
                }
                return failureCount < 3
            }
        },
        mutations: {
            retry: false
        }
    }
})

export default function Providers({ children }: { children: ReactNode }) {
    const [mocksReady, setMocksReady] = useState(!shouldEnableMocks)

    useEffect(() => {
        if (!shouldEnableMocks) return

        let active = true
        import('@/mocks')
            .then(({ enableMocking }) => enableMocking())
            .then(() => {
                if (active) setMocksReady(true)
            })
            .catch((error) => console.error(error))

        return () => {
            active = false
        }
    }, [])

    if (!mocksReady) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner />
            </div>
        )
    }

    return (
        <ObservabilityProvider>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </ObservabilityProvider>
    )
}
